import { axiosInstance } from 'api/axios';

/**
 * Upload a single file to S3 via backend API
 * @param {File} file - The file object to upload
 * @returns {Promise<Object>} Object containing file metadata: { fileName, fileSize, fileType, url }
 */
export async function uploadFileToS3(file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosInstance.post('/upload/single', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!response.status) {
    throw new Error(response.message || 'File upload failed');
  }

  // Map backend response to match existing frontend structure
  return {
    fileName: response.file.originalName,
    fileSize: response.file.size,
    fileType: response.file.mimetype.split('/')[1],
    url: response.file.location,
    result: response,
  };
}

/**
 * Upload multiple files to S3 via backend API
 * @param {File[]} files - Array of file objects to upload
 * @returns {Promise<Object[]>} Array of objects containing file metadata
 */
export async function uploadMultipleFiles(files) {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await axiosInstance.post('/upload/multiple', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!response.status) {
    throw new Error(response.message || 'File upload failed');
  }

  // Map backend response to match existing frontend structure
  return response.files.map((file) => ({
    fileName: file.originalName,
    fileSize: file.size,
    fileType: file.mimetype.split('/')[1],
    url: file.location,
    key: file.key,
  }));
}

/**
 * Get download URL for a file from S3 via backend API
 * @param {string} fileUrl - The full S3 URL or file key
 * @returns {Promise<string>} Pre-signed download URL
 */
export async function getDownloadUrl(fileUrl) {
  try {
    // Extract file key from URL if full URL is provided
    let fileKey = fileUrl.includes('amazonaws.com')
      ? fileUrl.split('/').pop()
      : fileUrl;
    
    // Decode URL-encoded characters in the file key
    fileKey = decodeURIComponent(fileKey);
    
    // URL-encode the file key for the API endpoint to handle special characters
    const encodedFileKey = encodeURIComponent(fileKey);

    const response = await axiosInstance.get(`/upload/download/${encodedFileKey}`);

    if (!response.status) {
      throw new Error(response.message || 'Failed to get download URL');
    }

    return response.url;
  } catch (error) {
    // If API fails and we have a full S3 URL, return it directly as fallback
    // (since files are uploaded with public-read ACL)
    if (fileUrl.includes('amazonaws.com')) {
      console.warn('API download failed, using direct S3 URL:', error);
      return fileUrl;
    }
    throw error;
  }
}

/**
 * Delete a file from S3 via backend API
 * @param {string} fileUrl - The full S3 URL or file key
 * @returns {Promise<Object>} Response object
 */
export async function deleteFile(fileUrl) {
  // Extract file key from URL if full URL is provided
  const fileKey = fileUrl.includes('amazonaws.com')
    ? fileUrl.split('/').pop()
    : fileUrl;

  const response = await axiosInstance.delete(`/upload/${fileKey}`);

  if (!response.status) {
    throw new Error(response.message || 'File deletion failed');
  }

  return response;
}

export default uploadFileToS3;

